import { isRouteHandle } from "@sg-protect/federation-runtime";
import { useMemo } from "react";
import { Navigate, generatePath, useMatches, useParams } from "react-router";
import { useHasActiveProtectAccess } from "./useHasActiveProtectAccess.ts";
import { getHomePaths } from "./homePaths.ts";
import { useTenants } from "./TenantsContext.tsx";
import { useWorkspaceId } from "./WorkspaceContext.tsx";

// Consented, with a data region and a primary tenant. Every gate that sends users between Home and a zero state reads
// this one answer, so two of them cannot disagree and loop.
export function useIsOnboardingComplete(): boolean {
    const hasActiveProtectAccess = useHasActiveProtectAccess();
    const { tenants } = useTenants();

    return hasActiveProtectAccess && tenants?.primaryTenant?.isConsented === true && tenants.region !== null;
}

export function useOnboardingRouteInfo() {
    const matches = useMatches();
    const lastHandle = matches.at(-1)?.handle;
    const isOnZeroStateRoute = isRouteHandle(lastHandle) ? (lastHandle.isZeroState ?? false) : false;
    const isOnboardingStep = isRouteHandle(lastHandle) ? (lastHandle.isOnboardingStep ?? false) : false;
    const isOnRestrictedStateRoute = isRouteHandle(lastHandle) ? (lastHandle.isRestrictedState ?? false) : false;

    const reversedMatches = matches.toReversed();

    const zeroStatePath = reversedMatches
        .map(m => (isRouteHandle(m.handle) ? m.handle.zeroStatePath : undefined))
        .find(p => p !== undefined);

    const restrictedPath = reversedMatches
        .map(m => (isRouteHandle(m.handle) ? m.handle.restrictedStatePath : undefined))
        .find(p => p !== undefined);

    return { isOnZeroStateRoute, isOnboardingStep, isOnRestrictedStateRoute, zeroStatePath, restrictedPath };
}

function useZeroStateRedirectPath(): string {
    const { zeroStatePath } = useOnboardingRouteInfo();
    const { workspaceId } = useParams<{ workspaceId: string }>();
    const homePaths = getHomePaths(useWorkspaceId());

    return generatePath(zeroStatePath ? zeroStatePath : homePaths.zeroState, { workspaceId });
}

function useRestrictedStateRedirectPath(): string {
    const { restrictedPath } = useOnboardingRouteInfo();
    const { workspaceId } = useParams<{ workspaceId: string }>();
    const homePaths = getHomePaths(useWorkspaceId());

    return generatePath(restrictedPath ? restrictedPath : homePaths.restrictedState, { workspaceId });
}

function useMissingLicenseStateRedirectPath(): string {
    const { zeroStatePath } = useOnboardingRouteInfo();
    const { workspaceId } = useParams<{ workspaceId: string }>();
    const homePaths = getHomePaths(useWorkspaceId());

    return generatePath(zeroStatePath ? zeroStatePath : homePaths.zeroState, { workspaceId });
}

/**
 * Onboarding routes a not-yet-ready workspace is allowed to sit on, so the gates render them instead
 * of enforcing their prerequisite: the zero-state `/get-started` pages, the section's restricted page,
 * and the standalone setup / no-access / missing-license pages (prevents redirect loops on the very
 * pages that let the user fix the gap).
 */
export function useIsOnboardingRoute(): boolean {
    const { isOnZeroStateRoute, isOnboardingStep, isOnRestrictedStateRoute } = useOnboardingRouteInfo();

    if (isOnboardingStep || isOnZeroStateRoute || isOnRestrictedStateRoute) {
        return true;
    }

    return false;
}

/**
 * Redirects a not-ready workspace into onboarding: the section's zero-state page when one exists,
 * otherwise Home's zero-state page.
 */
export function useGetOnboardingRedirectPaths() {
    const zeroStateRedirectPath = useZeroStateRedirectPath();
    const restrictedRedirectPath = useRestrictedStateRedirectPath();
    const missingLicenseRedirectPath = useMissingLicenseStateRedirectPath();

    const result = useMemo(
        () => ({
            notConsented: zeroStateRedirectPath,
            noAccess: restrictedRedirectPath,
            missingLicense: missingLicenseRedirectPath
        }),
        [zeroStateRedirectPath, restrictedRedirectPath, missingLicenseRedirectPath]
    );

    return result;
}

export function NotConsentedRedirect() {
    const to = useGetOnboardingRedirectPaths();

    return <Navigate to={to.notConsented} replace />;
}

export function NoAccessRedirect() {
    const to = useGetOnboardingRedirectPaths();

    return <Navigate to={to.noAccess} replace />;
}

export function MissingLicenseRedirect() {
    const to = useGetOnboardingRedirectPaths();

    return <Navigate to={to.missingLicense} replace />;
}
